import { createSlice } from '@reduxjs/toolkit';

export const cartSlice = createSlice({
    name: 'cart',
    initialState: {
        cartItems: [],
        open: false,
        openSmallCart: false,
        currentStep: 1,
        tempRate: { rateID: null, roomTypeID: null },
        hasExtras: false,
        isCartCompleted: false,
        isCartMobileOpen: false,
        isAddingRate: false,
        payDirectly: '',
        finalPrices: { totalPayment: 0, paymentAmount: 0, cartPolicy: '' },
    },
    reducers: {
        setCurrentStep: (state, { payload }) => {
            state.currentStep = payload.currentStep;
        },
        setTempRate: (state, { payload }) => {
            state.tempRate.rateID = payload.tempRate;
            state.tempRate.roomTypeID = payload.roomTypeID;
        },
        resetTempRate: (state, { payload }) => {
            state.tempRate.rateID = null;
            state.tempRate.roomTypeID = null;
        },
        addCartItem: (state, { payload }) => {
            const existingItemIndex = state.cartItems.findIndex(
                (item) =>
                    item.rateID === payload.rateID &&
                    item.roomTypeID === payload.roomTypeID
            );

            if (existingItemIndex !== -1) {
                state.cartItems[existingItemIndex] = {
                    ...state.cartItems[existingItemIndex],
                    ...payload,
                };
            } else {
                state.cartItems.push(payload);
            }
        },
        addCartItemExtra: (state, { payload }) => {
            const updatedCartItems = state.cartItems.map((cartItem) => {
                if (
                    cartItem.cartID === payload.cartID &&
                    cartItem.rateID === payload.rateID &&
                    cartItem.roomTypeID === payload.roomTypeID
                ) {
                    return {
                        ...cartItem,
                        extras: cartItem.extras
                            ? [...cartItem.extras, payload]
                            : [payload],
                    };
                }
                return cartItem;
            });

            state.cartItems = updatedCartItems;
        },
        updateCartItem: (state, { payload }) => {
            const updatedCartItems = state.cartItems.map((cartItem) => {
                if (cartItem.roomTypeID === payload.roomTypeID) {
                    return { ...cartItem, ...payload };
                }
                return cartItem;
            });

            state.cartItems = updatedCartItems;
        },
        updateCartItemExtra: (state, { payload }) => {
            const updatedCartItems = state.cartItems.map((cartItem) => {
                if (
                    cartItem.rateID === payload.rateID &&
                    cartItem.roomTypeID === payload.roomTypeID
                ) {
                    const updatedExtras = cartItem.extras
                        ? cartItem.extras.map((extra) => {
                              if (extra.addOnID === payload.addOnID) {
                                  return {
                                      ...extra,
                                      quantity: payload.quantity,
                                  };
                              }
                              return extra;
                          })
                        : [];

                    return {
                        ...cartItem,
                        extras: updatedExtras,
                    };
                }
                return cartItem;
            });

            state.cartItems = updatedCartItems;
        },
        removeCartItemExtra: (state, { payload }) => {
            const updatedCartItems = state.cartItems.map((cartItem) => {
                if (
                    cartItem.rateID === payload.rateID &&
                    cartItem.roomTypeID === payload.roomTypeID
                ) {
                    return {
                        ...cartItem,
                        extras: cartItem.extras
                            ? cartItem.extras.filter(
                                  (extra) => extra.addOnID !== payload.addOnID
                              )
                            : [],
                    };
                }
                return cartItem;
            });

            state.cartItems = updatedCartItems;
        },
        removeCartItem: (state, { payload }) => {
            const itemId = payload;
            state.cartItems = state.cartItems.filter(
                (cartItem) => cartItem.rateID !== itemId
            );
        },
        setItemIsCompleted: (state, { payload }) => {
            const updatedCartItems = state.cartItems.map((cartItem) => {
                if (
                    cartItem.rateID === payload.rateID &&
                    cartItem.roomTypeID === payload.roomTypeID
                ) {
                    return { ...cartItem, isCompleted: true };
                }
                return cartItem;
            });
            state.cartItems = updatedCartItems;
        },
        setItemIsNotCompleted: (state, { payload }) => {
            const updatedCartItems = state.cartItems.map((cartItem) => {
                if (
                    cartItem.rateID === payload.rateID &&
                    cartItem.roomTypeID === payload.roomTypeID
                ) {
                    return { ...cartItem, isCompleted: false };
                }
                return cartItem;
            });
            state.cartItems = updatedCartItems;
        },
        clearCart: (state) => {
            state.cartItems = [];
            state.open = false;
            state.openSmallCart = false;
            state.currentStep = 1;
            state.tempRate = { rateID: null, roomTypeID: null };
            state.hasExtras = false;
            state.isCartCompleted = false;
            state.isCartMobileOpen = false;
            state.isAddingRate = false;
            state.payDirectly = '';
            state.finalPrices = {
                totalPayment: 0,
                paymentAmount: 0,
                cartPolicy: '',
            };
        },
        setIsCompleteCart: (state, { payload }) => {
            state.isCartCompleted = true;
        },
        setCartOpenState: (state, { payload }) => {
            state.open = payload;
        },
        setCartMobileOpen: (state, { payload }) => {
            state.isCartMobileOpen = payload;
        },
        setAddingRate: (state, { payload }) => {
            state.isAddingRate = payload;
        },
        setSmallCartOpenState: (state, { payload }) => {
            state.openSmallCart = payload;
        },
        setHasExtras: (state, { payload }) => {
            state.hasExtras = payload;
        },
        setCartItems: (state, { payload }) => {
            state.cartItems = payload;
        },
        resetIsCompleteCart: (state) => {
            const updatedCartItems = state.cartItems.map((cartItem) => {
                return { ...cartItem, isCompleted: false };
            });
            state.cartItems = updatedCartItems;
        },
        setIsAllItemsCompleteCart: (state) => {
            const updatedCartItems = state.cartItems.map((cartItem) => {
                return { ...cartItem, isCompleted: true };
            });
            state.cartItems = updatedCartItems;
        },
        setCartPayDirectly: (state, { payload }) => {
            state.payDirectly = payload;
        },
        setFinalPrices: (state, { payload }) => {
            state.finalPrices.totalPayment = payload.totalPayment;
            state.finalPrices.paymentAmount = payload.paymentAmount;
            state.finalPrices.cartPolicy = payload.cartPolicy;
        },
    },
});

export const {
    setCurrentStep,
    addCartItem,
    clearCart,
    removeCartItem,
    setCartOpenState,
    setSmallCartOpenState,
    setCartItems,
    setItemIsCompleted,
    addCartItemExtra,
    removeCartItemExtra,
    resetIsCompleteCart,
    updateCartItemExtra,
    updateCartItem,
    setTempRate,
    resetTempRate,
    setItemIsNotCompleted,
    setHasExtras,
    setIsCompleteCart,
    setIsAllItemsCompleteCart,
    setCartMobileOpen,
    setAddingRate,
    setCartPayDirectly,
    setFinalPrices,
} = cartSlice.actions;
